import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Trash2, Check, X, ChevronDown, Mail, Phone, MapPin, GraduationCap, CreditCard, Package, Calendar as CalIcon, Scissors } from "lucide-react";
import AdminMediaTab from "@/components/admin/AdminMediaTab";

const TABS = ["Overview", "Products", "Brands", "Lookbook", "VIP List", "Emails", "Bookings", "Locks", "Moderation", "Orders", "Donations", "Media"];

export default function Admin() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("Overview");
  const [stats, setStats] = useState(null);
  const [products, setProducts] = useState([]);
  const [locks, setLocks] = useState([]);
  const [mod, setMod] = useState([]);
  const [orders, setOrders] = useState([]);
  const [donations, setDonations] = useState([]);
  const [tryons, setTryons] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [brands, setBrands] = useState([]);
  const [vip, setVip] = useState({ total: 0, active: 0, subscribers: [] });
  const [emailTemplates, setEmailTemplates] = useState([]);
  const [emailPreviewKey, setEmailPreviewKey] = useState(null);
  const [emailPreviewHtml, setEmailPreviewHtml] = useState("");
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [form, setForm] = useState({ name: "", category: "dresses", occasion: "prom", price: "", color: "", description: "", images: "", sizes: "XS,S,M,L" });

  useEffect(() => { if (!loading && (!user || user.role !== "admin")) navigate("/"); }, [loading, user, navigate]);

  useEffect(() => {
    if (!user) return;
    api.adminStats().then(({ data }) => setStats(data)).catch(() => {});
    api.adminProducts().then(({ data }) => setProducts(data)).catch(() => {});
    api.adminLocks().then(({ data }) => setLocks(data)).catch(() => {});
    api.moderation().then(({ data }) => setMod(data)).catch(() => {});
    api.adminOrders().then(({ data }) => setOrders(data)).catch(() => {});
    api.adminDonations().then(({ data }) => setDonations(data)).catch(() => {});
    api.adminTryons().then(({ data }) => setTryons(data)).catch(() => {});
    api.adminBookings().then(({ data }) => setBookings(data)).catch(() => {});
    api.adminBrands().then(({ data }) => setBrands(data)).catch(() => {});
    api.adminVipSubscribers().then(({ data }) => setVip(data)).catch(() => {});
    api.adminEmailPreviews().then(({ data }) => setEmailTemplates(data.templates || [])).catch(() => {});
  }, [user, tab]);

  const BACKEND = process.env.REACT_APP_BACKEND_URL;
  const toggleFeature = async (t) => {
    try {
      await api.featureTryon(t.id, !t.featured_lookbook);
      setTryons((list) => list.map((x) => x.id === t.id ? { ...x, featured_lookbook: !x.featured_lookbook } : x));
      toast.success(!t.featured_lookbook ? "Locked into the Lookbook" : "Removed from Lookbook");
    } catch (e) { toast.error(errMsg(e)); }
  };

  if (!user || user.role !== "admin") return null;

  const addProduct = async () => {
    try {
      await api.createProduct({ ...form, price: Number(form.price), images: form.images.split(",").map((s) => s.trim()).filter(Boolean), sizes: form.sizes.split(",").map((s) => s.trim()) });
      toast.success("Product added"); api.adminProducts().then(({ data }) => setProducts(data));
      setForm({ ...form, name: "", price: "", color: "", description: "", images: "" });
    } catch (e) { toast.error(errMsg(e)); }
  };
  const del = async (id) => { await api.deleteProduct(id); setProducts((p) => p.filter((x) => x.id !== id)); };

  const onImport = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImporting(true); setImportResult(null);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.importProducts(fd);
      setImportResult(data);
      toast.success(`Imported ${data.created} products`);
      api.adminProducts().then(({ data }) => setProducts(data));
    } catch (err) { toast.error(errMsg(err)); } finally { setImporting(false); e.target.value = ""; }
  };

  const downloadTemplate = async () => {
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/products/import-template`, { headers: { Authorization: `Bearer ${localStorage.getItem("hb_token")}` } });
      const text = await res.text();
      const blob = new Blob([text], { type: "text/csv" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = "heathers-product-template.csv"; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Could not download template"); }
  };
  const override = async (id) => { await api.overrideLock(id); setLocks((l) => l.map((x) => x.id === id ? { ...x, status: "released" } : x)); toast("Lock released"); };
  const moderate = async (id, approve) => { await api.moderate(id, approve); setMod((m) => m.filter((x) => x.id !== id)); };

  return (
    <div data-testid="admin-page" className="max-w-[1300px] mx-auto px-5 sm:px-8 pt-12">
      <p className="font-label text-[10px] text-rosemetal mb-2">Operations</p>
      <h1 className="font-display text-5xl sm:text-6xl mb-10">Admin</h1>

      <div className="flex gap-6 border-b border-border mb-10 overflow-x-auto">
        {TABS.map((t) => <button key={t} data-testid={`admin-tab-${t.toLowerCase().replace(/\s+/g,"-")}`} onClick={() => setTab(t)} className={`font-label text-[10px] pb-4 -mb-px border-b-2 whitespace-nowrap ${tab === t ? "border-rosemetal text-ink" : "border-transparent text-muted-foreground"}`}>{t}</button>)}
      </div>

      {tab === "Overview" && stats && (
        <div data-testid="admin-overview">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {Object.entries(stats).map(([k, v]) => (
              <div key={k} className="border border-border rounded-sm p-6">
                <div className="font-display text-4xl">{k.includes("revenue") || k.includes("donations") ? `$${v}` : v}</div>
                <div className="font-label text-[9px] text-muted-foreground mt-1 capitalize">{k.replace(/_/g, " ")}</div>
              </div>
            ))}
          </div>
          <SchoolDistancesPanel />
          <IndexNowPanel />
          <ContentGenPanel />
        </div>
      )}

      {tab === "Products" && (
        <div data-testid="admin-products">
          <div className="border border-rosemetal/40 bg-blush/40 rounded-sm p-6 mb-8">
            <h3 className="font-display text-2xl mb-2">Bulk import from CSV</h3>
            <p className="font-body text-xs text-muted-foreground mb-4">Upload a CSV to add hundreds of dresses at once. Columns: name, category, occasion, price, color, description, image_urls (| separated), sizes (| separated), silhouette, neckline, length, sleeve, designer, trend, featured, new_arrival.</p>
            <div className="flex flex-wrap items-center gap-3">
              <label className="bg-ink text-ivory px-5 py-2.5 font-label text-[10px] rounded-sm cursor-pointer">
                {importing ? "Importing…" : "Choose CSV & Import"}
                <input data-testid="import-csv-input" type="file" accept=".csv" className="hidden" onChange={onImport} disabled={importing} />
              </label>
              <a href={`${process.env.REACT_APP_BACKEND_URL}/api/admin/products/import-template`} className="font-label text-[9px] text-rosemetal underline" onClick={(e) => { e.preventDefault(); downloadTemplate(); }}>Download template</a>
              {importResult && <span data-testid="import-result" className="font-label text-[9px] text-[hsl(var(--accent))]">Added {importResult.created}{importResult.errors?.length ? ` · ${importResult.errors.length} errors` : ""}</span>}
            </div>
          </div>
          <div className="border border-border rounded-sm p-6 mb-8 grid md:grid-cols-2 gap-3">
            <input data-testid="prod-name" placeholder="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="border border-border rounded-sm px-3 py-2 text-sm" />
            <input data-testid="prod-price" placeholder="Price" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} className="border border-border rounded-sm px-3 py-2 text-sm" />
            <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="border border-border rounded-sm px-3 py-2 text-sm">{["dresses", "tuxedos", "accessories"].map((c) => <option key={c}>{c}</option>)}</select>
            <select value={form.occasion} onChange={(e) => setForm({ ...form, occasion: e.target.value })} className="border border-border rounded-sm px-3 py-2 text-sm">{["prom", "graduation", "homecoming"].map((c) => <option key={c}>{c}</option>)}</select>
            <input placeholder="Color" value={form.color} onChange={(e) => setForm({ ...form, color: e.target.value })} className="border border-border rounded-sm px-3 py-2 text-sm" />
            <input placeholder="Sizes (comma sep)" value={form.sizes} onChange={(e) => setForm({ ...form, sizes: e.target.value })} className="border border-border rounded-sm px-3 py-2 text-sm" />
            <input placeholder="Image URL(s), comma sep" value={form.images} onChange={(e) => setForm({ ...form, images: e.target.value })} className="border border-border rounded-sm px-3 py-2 text-sm md:col-span-2" />
            <textarea placeholder="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="border border-border rounded-sm px-3 py-2 text-sm md:col-span-2" rows={2} />
            <button data-testid="add-product-btn" onClick={addProduct} className="bg-ink text-ivory py-3 font-label text-[10px] rounded-sm md:col-span-2">Add Product</button>
          </div>
          <div className="space-y-2">
            {products.map((p) => (
              <div key={p.id} className="flex items-center justify-between border border-border rounded-sm p-4">
                <div className="flex items-center gap-4"><img src={p.images[0]} alt="" className="w-12 h-16 object-cover object-top bg-blush" /><div><h4 className="font-display text-xl">{p.name}</h4><p className="font-body text-xs text-muted-foreground">{p.category} · ${p.price}</p></div></div>
                <button data-testid={`del-product-${p.id}`} onClick={() => del(p.id)} className="text-muted-foreground hover:text-[hsl(var(--destructive))]"><Trash2 className="w-4 h-4" /></button>
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === "Brands" && (
        <div data-testid="admin-brands" className="space-y-3">
          <p className="font-body text-sm text-muted-foreground mb-6">Manage designer logos, hero images, taglines & descriptions. Each brand's <code className="bg-blush px-1 py-0.5 rounded text-xs">/brand/{`{slug}`}</code> page pulls from here.</p>
          {brands.length === 0 ? (
            <p className="font-body text-muted-foreground">No brands yet — import products first.</p>
          ) : (
            <div className="space-y-4">
              {brands.map((b) => (
                <BrandRow key={b.brand_slug} brand={b} onSaved={(updated) => setBrands((prev) => prev.map((x) => x.brand_slug === updated.brand_slug ? { ...x, ...updated } : x))} />
              ))}
            </div>
          )}
        </div>
      )}

      {tab === "Lookbook" && (
        <div data-testid="admin-lookbook">
          <p className="font-body text-sm text-muted-foreground mb-6">Every AI try-on your students created. Lock the ones you love into the boutique Lookbook — credited to the student & their school.</p>
          {tryons.length === 0 ? <p className="font-body text-muted-foreground">No try-ons yet.</p> : (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {tryons.map((t) => (
                <div key={t.id} data-testid={`lookbook-item-${t.id}`} className="border border-border rounded-sm overflow-hidden group">
                  <div className="relative aspect-[3/4] bg-blush overflow-hidden">
                    <img src={`${BACKEND}${t.image_url}`} alt={t.product_name} className="w-full h-full object-cover object-top" />
                    {t.featured_lookbook && <span className="absolute top-2 left-2 font-label text-[8px] text-ink bg-champagne px-2 py-0.5 rounded-sm">In Lookbook</span>}
                    {!t.shareable && <span className="absolute top-2 right-2 font-label text-[8px] text-ivory bg-ink/70 px-2 py-0.5 rounded-sm">No consent</span>}
                  </div>
                  <div className="p-3">
                    <h4 className="font-display text-base leading-tight truncate">{t.product_name}</h4>
                    <p className="font-body text-[11px] text-muted-foreground mt-0.5 truncate">{t.user_name}{t.school ? ` · ${t.school}` : ""}</p>
                    <button
                      data-testid={`feature-tryon-${t.id}`}
                      onClick={() => toggleFeature(t)}
                      disabled={!t.shareable && !t.featured_lookbook}
                      className={`w-full mt-3 py-2 font-label text-[9px] rounded-sm transition-colors ${t.featured_lookbook ? "bg-rosemetal text-ivory" : "border border-ink hover:bg-ink hover:text-ivory disabled:opacity-40 disabled:cursor-not-allowed"}`}
                    >
                      {t.featured_lookbook ? "Locked In ✓" : t.shareable ? "Lock into Lookbook" : "Consent needed"}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {tab === "VIP List" && (
        <div data-testid="admin-vip-list" className="space-y-6">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <div className="border border-border rounded-sm p-6 bg-blush/30">
              <div className="font-display text-4xl text-rosemetal">{vip.total}</div>
              <div className="font-label text-[9px] text-muted-foreground mt-1 tracking-widest">TOTAL VIP SIGNUPS</div>
            </div>
            <div className="border border-border rounded-sm p-6">
              <div className="font-display text-4xl">{vip.active}</div>
              <div className="font-label text-[9px] text-muted-foreground mt-1 tracking-widest">ACTIVE (SUBSCRIBED)</div>
            </div>
            <div className="border border-border rounded-sm p-6">
              <button
                onClick={() => {
                  const rows = [
                    ["email", "name", "source", "created_at", "welcome_ok"],
                    ...vip.subscribers.map((s) => [s.email, s.name || "", s.source || "", s.created_at || "", s.welcome_email_ok ? "yes" : "no"]),
                  ];
                  const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
                  const blob = new Blob([csv], { type: "text/csv" });
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement("a");
                  a.href = url; a.download = `vip-list-${new Date().toISOString().slice(0,10)}.csv`; a.click();
                  URL.revokeObjectURL(url);
                }}
                data-testid="vip-export-csv"
                className="w-full border-2 border-ink hover:bg-ink hover:text-ivory transition-colors font-label text-[10px] tracking-widest py-3 rounded-sm"
              >
                EXPORT CSV
              </button>
              <p className="font-label text-[9px] text-muted-foreground mt-2 tracking-widest">FOR MAILCHIMP · KLAVIYO · GOHIGHLEVEL</p>
            </div>
          </div>

          <div className="border border-border rounded-sm overflow-hidden">
            <div className="grid grid-cols-[1fr_140px_120px_100px] gap-4 px-5 py-3 bg-blush/40 font-label text-[9px] tracking-widest text-ink/60">
              <span>EMAIL</span>
              <span>NAME</span>
              <span>SOURCE</span>
              <span className="text-right">JOINED</span>
            </div>
            {vip.subscribers.length === 0 ? (
              <p className="font-body text-sm text-muted-foreground px-5 py-8 text-center">No signups yet. The moment someone joins the footer VIP List, they'll show up here.</p>
            ) : (
              vip.subscribers.map((s) => (
                <div key={s.id} data-testid={`vip-row-${s.id}`} className="grid grid-cols-[1fr_140px_120px_100px] gap-4 px-5 py-3 border-t border-border items-center">
                  <a href={`mailto:${s.email}`} className="font-body text-sm text-ink hover:text-rosemetal truncate">{s.email}</a>
                  <span className="font-body text-sm text-ink/70 truncate">{s.name || "—"}</span>
                  <span className="font-label text-[9px] text-ink/50 tracking-widest">{(s.source || "footer").toUpperCase()}</span>
                  <span className="font-label text-[9px] text-ink/50 tracking-widest text-right">{s.created_at ? new Date(s.created_at).toLocaleDateString() : "—"}</span>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {tab === "Emails" && (
        <div data-testid="admin-emails" className="space-y-6">
          <div>
            <p className="font-label text-[10px] tracking-widest text-rosemetal">TEMPLATE PREVIEWS</p>
            <h3 className="font-display text-3xl mt-1 mb-2">Every transactional email, before it hits inboxes.</h3>
            <p className="font-body text-sm text-ink/60 max-w-xl">Pick a template to preview the rendered HTML with sample data. Same markup that ships to real customers.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
            {emailTemplates.map((t) => (
              <button
                key={t.key}
                onClick={async () => {
                  setEmailPreviewKey(t.key);
                  setEmailPreviewHtml("<div style='padding:24px;font-family:sans-serif;'>Loading…</div>");
                  try {
                    const token = localStorage.getItem("hb_token") || "";
                    const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/email-previews/${t.key}`, {
                      headers: { Authorization: `Bearer ${token}` },
                    });
                    setEmailPreviewHtml(await r.text());
                  } catch { setEmailPreviewHtml("<div style='padding:24px;color:#c00;'>Failed to load preview.</div>"); }
                }}
                data-testid={`email-preview-${t.key}`}
                className={`text-left border rounded-sm p-4 hover:-translate-y-0.5 transition-transform ${emailPreviewKey === t.key ? "border-rosemetal bg-blush/20" : "border-border hover:border-rosemetal"}`}
              >
                <p className="font-label text-[9px] tracking-widest text-rosemetal">{t.key.toUpperCase()}</p>
                <p className="font-display text-lg mt-1">{t.name}</p>
                <p className="font-body text-xs text-ink/50 mt-2 leading-relaxed">{t.note}</p>
              </button>
            ))}
          </div>
          {emailPreviewKey && (
            <div className="border border-border rounded-sm overflow-hidden bg-ink/5">
              <div className="flex items-center justify-between px-4 py-2.5 border-b border-border bg-ivory">
                <span className="font-label text-[10px] tracking-widest text-ink/60">PREVIEWING · {emailPreviewKey.toUpperCase()}</span>
                <button onClick={() => { setEmailPreviewKey(null); setEmailPreviewHtml(""); }} className="font-label text-[9px] tracking-widest text-ink/50 hover:text-rosemetal">CLOSE ✕</button>
              </div>
              <iframe
                title="email preview"
                data-testid="email-preview-iframe"
                srcDoc={emailPreviewHtml}
                className="w-full h-[720px] bg-white"
                sandbox="allow-same-origin"
              />
            </div>
          )}
        </div>
      )}

      {tab === "Bookings" && (
        <div data-testid="admin-bookings" className="space-y-3">
          <p className="font-body text-sm text-muted-foreground mb-2">Master Seamstress private fittings ($299) — Heather's daily appointment book.</p>
          {bookings.length === 0 ? <p className="font-body text-muted-foreground">No bookings yet.</p> : bookings.map((b) => (
            <div key={b.id} data-testid={`booking-${b.id}`} className="border border-border rounded-sm p-4 flex items-center justify-between gap-4">
              <div>
                <h4 className="font-display text-xl">{b.product_name}</h4>
                <p className="font-body text-xs text-muted-foreground mt-0.5">{b.user_name}{b.user_email ? ` · ${b.user_email}` : ""}</p>
                <p className="font-label text-[9px] text-rosemetal mt-1">{b.appointment ? new Date(b.appointment).toLocaleString() : "No time set"} · {b.address}</p>
              </div>
              <div className="text-right"><span className="font-display text-2xl">${b.fee}</span><p className="font-label text-[9px] text-muted-foreground capitalize">{b.status}</p></div>
            </div>
          ))}
        </div>
      )}

      {tab === "Locks" && (
        <div data-testid="admin-locks" className="space-y-2">
          {locks.length === 0 ? <p className="font-body text-muted-foreground">No locks.</p> : locks.map((l) => (
            <div key={l.id} className="flex items-center justify-between border border-border rounded-sm p-4">
              <div><h4 className="font-display text-xl">{l.product_name}</h4><p className="font-body text-xs text-muted-foreground">{l.user_name} · {l.school_id} · <span className="capitalize">{l.status}</span></p></div>
              {l.status === "active" && <button data-testid={`override-${l.id}`} onClick={() => override(l.id)} className="border border-ink px-4 py-2 font-label text-[9px] rounded-sm">Override</button>}
            </div>
          ))}
        </div>
      )}

      {tab === "Moderation" && (
        <div data-testid="admin-moderation" className="grid md:grid-cols-3 gap-4">
          {mod.length === 0 ? <p className="font-body text-muted-foreground">Queue is clear.</p> : mod.map((m) => (
            <div key={m.id} className="border border-border rounded-sm overflow-hidden">
              {m.memory && <img src={m.memory.image_url} alt="" className="w-full aspect-square object-cover" />}
              <div className="p-3"><p className="font-body text-sm">{m.memory?.caption}</p><p className="font-label text-[9px] text-muted-foreground mt-1">{m.memory?.author}</p>
                <div className="flex gap-2 mt-3">
                  <button data-testid={`approve-${m.id}`} onClick={() => moderate(m.id, true)} className="flex-1 bg-[hsl(var(--accent))] text-ivory py-2 rounded-sm flex items-center justify-center"><Check className="w-4 h-4" /></button>
                  <button data-testid={`reject-${m.id}`} onClick={() => moderate(m.id, false)} className="flex-1 border border-border py-2 rounded-sm flex items-center justify-center"><X className="w-4 h-4" /></button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === "Orders" && (
        <div data-testid="admin-orders" className="space-y-2">
          {orders.length === 0 ? <p className="font-body text-muted-foreground">No orders.</p> : orders.map((o) => (
            <OrderRow key={o.id} order={o} />
          ))}
        </div>
      )}

      {tab === "Donations" && (
        <div data-testid="admin-donations" className="space-y-2">
          {donations.length === 0 ? <p className="font-body text-muted-foreground">No donations.</p> : donations.map((d) => (
            <div key={d.id} className="border border-border rounded-sm p-4 flex justify-between"><div><p className="font-body text-sm">{d.donor_name}</p><p className="font-label text-[9px] text-muted-foreground">{d.receipt_no}</p></div><span className="font-display text-2xl">${d.amount}</span></div>
          ))}
        </div>
      )}

      {tab === "Media" && <AdminMediaTab />}
    </div>
  );
}

function BrandRow({ brand, onSaved }) {
  const [logo, setLogo] = useState(brand.logo_url || "");
  const [hero, setHero] = useState(brand.hero_url || "");
  const [tagline, setTagline] = useState(brand.tagline || "");
  const [description, setDescription] = useState(brand.description || "");
  const [saving, setSaving] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const dirty = logo !== (brand.logo_url || "") || hero !== (brand.hero_url || "") || tagline !== (brand.tagline || "") || description !== (brand.description || "");
  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.updateBrand(brand.brand_slug, {
        logo_url: logo || null,
        hero_url: hero || null,
        tagline: tagline || null,
        description: description || null,
      });
      onSaved({ brand_slug: brand.brand_slug, logo_url: data.logo_url, hero_url: data.hero_url, tagline: data.tagline, description: data.description });
      toast.success(`${brand.designer} updated`);
    } catch (e) { toast.error(errMsg(e)); } finally { setSaving(false); }
  };
  return (
    <div data-testid={`admin-brand-${brand.brand_slug}`} className="border border-border rounded-sm p-4">
      <div className="flex items-center gap-4">
        <div className="w-24 h-16 bg-blush/40 border border-border rounded-sm flex items-center justify-center flex-shrink-0 overflow-hidden">
          {logo ? <img src={logo} alt={brand.designer} className="max-h-full max-w-full object-contain" /> : <span className="font-label text-[9px] text-muted-foreground text-center px-1">{brand.designer}</span>}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline gap-3">
            <h3 className="font-display text-2xl leading-none">{brand.designer}</h3>
            <span className="font-label text-[9px] text-muted-foreground">/brand/{brand.brand_slug} · {brand.count} looks</span>
          </div>
          {tagline && <p className="font-body italic text-sm text-muted-foreground mt-1">"{tagline}"</p>}
        </div>
        <button data-testid={`brand-toggle-${brand.brand_slug}`} onClick={() => setExpanded((v) => !v)} className="font-label text-[10px] text-rosemetal">
          {expanded ? "Close" : "Edit"}
        </button>
      </div>
      {expanded && (
        <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-4">
          <label className="block">
            <span className="font-label text-[9px] text-muted-foreground">Logo URL <span className="text-[8px]">(transparent PNG or SVG recommended)</span></span>
            <input data-testid={`brand-logo-input-${brand.brand_slug}`} value={logo} onChange={(e) => setLogo(e.target.value)} placeholder="https://…/logo.svg" className="w-full mt-1 border border-border rounded-sm px-3 py-2 font-body text-sm" />
          </label>
          <label className="block">
            <span className="font-label text-[9px] text-muted-foreground">Hero image URL <span className="text-[8px]">(wide banner for /brand/… page)</span></span>
            <input data-testid={`brand-hero-input-${brand.brand_slug}`} value={hero} onChange={(e) => setHero(e.target.value)} placeholder="https://…/hero.jpg" className="w-full mt-1 border border-border rounded-sm px-3 py-2 font-body text-sm" />
          </label>
          <label className="block md:col-span-2">
            <span className="font-label text-[9px] text-muted-foreground">Tagline <span className="text-[8px]">(one line, shown under the logo)</span></span>
            <input data-testid={`brand-tagline-input-${brand.brand_slug}`} value={tagline} onChange={(e) => setTagline(e.target.value)} className="w-full mt-1 border border-border rounded-sm px-3 py-2 font-body text-sm" />
          </label>
          <label className="block md:col-span-2">
            <span className="font-label text-[9px] text-muted-foreground">Description <span className="text-[8px]">(2-3 sentences, shows on brand page)</span></span>
            <textarea data-testid={`brand-desc-input-${brand.brand_slug}`} value={description} onChange={(e) => setDescription(e.target.value)} rows={3} className="w-full mt-1 border border-border rounded-sm px-3 py-2 font-body text-sm" />
          </label>
          <div className="md:col-span-2 flex justify-end gap-2">
            <button onClick={() => { setLogo(brand.logo_url || ""); setHero(brand.hero_url || ""); setTagline(brand.tagline || ""); setDescription(brand.description || ""); }} className="border border-border px-4 py-2 font-label text-[10px] rounded-sm">Reset</button>
            <button data-testid={`brand-save-${brand.brand_slug}`} onClick={save} disabled={!dirty || saving} className="bg-ink text-ivory px-6 py-2 font-label text-[10px] rounded-sm disabled:opacity-50">
              {saving ? "Saving…" : "Save"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}


function OrderRow({ order: o }) {
  const [open, setOpen] = useState(false);
  const short = o.id?.slice(0, 8).toUpperCase();
  const c = o.customer || {};
  const p = o.payment || {};
  const when = new Date(o.created_at).toLocaleString();
  const paid = p.status === "paid";

  return (
    <div className="border border-border rounded-sm overflow-hidden bg-ivory" data-testid={`admin-order-${o.id}`}>
      {/* summary row */}
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        data-testid={`admin-order-toggle-${o.id}`}
        className="w-full flex items-center justify-between gap-4 px-4 py-3 hover:bg-cream/40 transition-colors text-left"
      >
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className="font-label text-[9px] tracking-widest text-muted-foreground">#{short}</span>
            <span className={`font-label text-[9px] tracking-widest px-1.5 py-0.5 rounded-sm ${paid ? "bg-champagne/40 text-ink" : "bg-muted text-muted-foreground"}`}>
              {(p.status || o.status || "—").toUpperCase()}
            </span>
            {p.seamstress && (
              <span className="font-label text-[9px] tracking-widest px-1.5 py-0.5 rounded-sm bg-rosemetal text-cream flex items-center gap-1">
                <Scissors className="w-2.5 h-2.5" /> SEAMSTRESS
              </span>
            )}
          </div>
          <p className="font-body text-sm truncate">
            {c.name ? <b>{c.name}</b> : <span className="text-muted-foreground">Guest</span>}
            <span className="text-muted-foreground"> · </span>
            {o.items.map((i) => i.name).join(", ")}
          </p>
          <p className="font-label text-[9px] text-muted-foreground mt-0.5">
            {when}{c.school_name ? ` · ${c.school_name}` : ""}{c.city ? ` · ${c.city}, WI` : ""}
          </p>
        </div>
        <div className="flex items-center gap-3 shrink-0">
          <span className="font-display text-2xl tabular-nums">${o.total}</span>
          <ChevronDown className={`w-4 h-4 text-muted-foreground transition-transform ${open ? "rotate-180" : ""}`} />
        </div>
      </button>

      {/* expanded detail */}
      {open && (
        <div className="border-t border-border bg-cream/30 p-5 grid md:grid-cols-3 gap-6" data-testid={`admin-order-detail-${o.id}`}>
          {/* Customer */}
          <div>
            <p className="font-label text-[9px] tracking-widest text-rosemetal mb-3">CUSTOMER</p>
            <p className="font-display text-lg leading-tight mb-2">{c.name || "—"}</p>
            <ul className="space-y-1.5 font-body text-xs text-ink">
              {c.email && (
                <li className="flex items-center gap-2">
                  <Mail className="w-3 h-3 text-muted-foreground shrink-0" />
                  <a href={`mailto:${c.email}`} className="hover:text-rosemetal truncate">{c.email}</a>
                </li>
              )}
              {c.phone && (
                <li className="flex items-center gap-2">
                  <Phone className="w-3 h-3 text-muted-foreground shrink-0" />
                  <a href={`tel:${c.phone}`} className="hover:text-rosemetal">{c.phone}</a>
                </li>
              )}
              {(c.address || c.city || c.zip) && (
                <li className="flex items-start gap-2">
                  <MapPin className="w-3 h-3 text-muted-foreground shrink-0 mt-0.5" />
                  <span>{[c.address, c.city, c.zip && `WI ${c.zip}`].filter(Boolean).join(", ")}</span>
                </li>
              )}
              {c.school_name && (
                <li className="flex items-center gap-2">
                  <GraduationCap className="w-3 h-3 text-muted-foreground shrink-0" />
                  {c.school_name}{c.school_city ? ` · ${c.school_city}` : ""}
                </li>
              )}
            </ul>
          </div>

          {/* Items */}
          <div className="md:col-span-2">
            <p className="font-label text-[9px] tracking-widest text-rosemetal mb-3">
              <Package className="w-3 h-3 inline mr-1 -mt-0.5" strokeWidth={1.75} /> ITEMS · {o.items.length}
            </p>
            <div className="space-y-2">
              {o.items.map((it, idx) => (
                <div key={idx} className="flex items-center gap-3 bg-ivory rounded-sm p-2 border border-border">
                  {it.image && (
                    <img src={it.image} alt={it.name} className="w-12 h-16 object-cover object-top bg-blush shrink-0 rounded-sm" />
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="font-body text-sm truncate">{it.name}</p>
                    <p className="font-label text-[9px] text-muted-foreground tracking-widest">
                      {[it.color, it.size && `Size ${it.size}`, `Qty ${it.quantity}`].filter(Boolean).join(" · ")}
                    </p>
                  </div>
                  <div className="font-body text-sm tabular-nums shrink-0">
                    ${(it.price * it.quantity).toFixed(0)}
                  </div>
                </div>
              ))}
            </div>

            {/* Payment + Fitting */}
            <div className="mt-4 grid sm:grid-cols-2 gap-4 pt-4 border-t border-border">
              <div>
                <p className="font-label text-[9px] tracking-widest text-rosemetal mb-2">
                  <CreditCard className="w-3 h-3 inline mr-1 -mt-0.5" strokeWidth={1.75} /> PAYMENT
                </p>
                <ul className="space-y-1 font-body text-xs">
                  <li>Status: <b className={paid ? "text-rosemetal" : ""}>{(p.status || o.status || "—").toUpperCase()}</b></li>
                  <li>Provider: {p.provider || "stripe"}</li>
                  <li>Total: <b>${o.total}</b></li>
                  <li className="text-muted-foreground text-[10px]">Session: {o.session_id?.slice(0, 22)}…</li>
                </ul>
              </div>
              {(p.seamstress || p.appointment || p.fit) && (
                <div>
                  <p className="font-label text-[9px] tracking-widest text-rosemetal mb-2">
                    <CalIcon className="w-3 h-3 inline mr-1 -mt-0.5" strokeWidth={1.75} /> PRIVATE FITTING
                  </p>
                  <ul className="space-y-1 font-body text-xs">
                    {p.appointment && <li>When: <b>{String(p.appointment).replace("T", " at ")}</b></li>}
                    {p.fit && <li>Fit type: <b>{p.fit}</b></li>}
                  </ul>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


/* ------------------------------------------------------------------ */
/* School distances recompute panel                                    */
/* ------------------------------------------------------------------ */
function SchoolDistancesPanel() {
  const [state, setState] = useState({ status: "idle", result: null });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const API = process.env.REACT_APP_BACKEND_URL;
  const token = () => (localStorage.getItem("hb_token") || "").trim();

  const fetchStatus = async () => {
    try {
      const r = await fetch(`${API}/api/embed/admin/recompute-school-distances/status`, {
        headers: { Authorization: `Bearer ${token()}` },
      });
      if (!r.ok) return;
      const d = await r.json();
      setState(d || { status: "idle", result: null });
    } catch (_) { /* ignore transient network errors */ }
  };

  useEffect(() => { fetchStatus(); }, []);

  useEffect(() => {
    if (state.status !== "running") return;
    const iv = setInterval(fetchStatus, 4000);
    return () => clearInterval(iv);
  }, [state.status]);

  const run = async () => {
    setBusy(true);
    setErr("");
    try {
      const r = await fetch(`${API}/api/embed/admin/recompute-school-distances`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token()}` },
      });
      if (!r.ok) throw new Error(`http ${r.status}`);
      await r.json();
      setState({ status: "running", result: null });
      setTimeout(fetchStatus, 1500);
    } catch (e) {
      setErr(String(e).slice(0, 200));
    } finally {
      setBusy(false);
    }
  };

  const r = state.result || {};
  const badgeColor =
    state.status === "running" ? "bg-blush text-ink" :
    state.status === "done" ? "bg-ink text-ivory" :
    state.status === "error" ? "bg-[hsl(var(--destructive))] text-white" :
    "bg-border text-muted-foreground";

  return (
    <div data-testid="school-distances-panel" className="mt-10 border border-border rounded-sm p-6">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="font-label text-[10px] tracking-widest text-rosemetal mb-1">
            ◉ SCHOOL DISTANCES
          </div>
          <h3 className="font-display text-3xl mb-1">Recompute driving miles</h3>
          <p className="font-body text-xs text-muted-foreground max-w-lg">
            Recalculates driving distance (miles) from Heather&apos;s Boutique to every school using OSRM real road-routing.
            Idempotent, safe to re-run after any school import. Takes about 90 seconds.
          </p>
        </div>
        <button
          data-testid="recompute-school-distances-btn"
          onClick={run}
          disabled={busy || state.status === "running"}
          className="bg-ink text-ivory px-6 py-3 font-label text-[10px] tracking-widest rounded-sm disabled:opacity-50"
        >
          {state.status === "running" ? "Running…" : busy ? "Starting…" : "Recompute Now"}
        </button>
      </div>

      <div className="mt-5 flex items-center gap-3 flex-wrap">
        <span data-testid="job-status" className={`inline-block px-3 py-1 rounded-full font-label text-[9px] tracking-widest ${badgeColor}`}>
          {(state.status || "idle").toUpperCase()}
        </span>
        {state.status === "running" && (
          <span className="font-body text-xs text-muted-foreground">Working through 258 schools… polling every 4s.</span>
        )}
        {state.status === "done" && r && (
          <span data-testid="job-result" className="font-body text-xs text-muted-foreground">
            Updated <b className="text-ink">{r.updated ?? 0}</b> · unchanged <b className="text-ink">{r.unchanged ?? 0}</b>
            {r.fallbacks ? <> · fallbacks <b>{r.fallbacks}</b></> : null}
            {r.failed_count ? <> · failed <b>{r.failed_count}</b></> : null}
            {state.finished_at ? <> · finished {new Date(state.finished_at).toLocaleTimeString()}</> : null}
          </span>
        )}
        {state.status === "error" && r?.error && (
          <span className="font-body text-xs text-[hsl(var(--destructive))]">Error: {r.error}</span>
        )}
        {err && <span className="font-body text-xs text-[hsl(var(--destructive))]">{err}</span>}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* IndexNow ping panel                                                 */
/* ------------------------------------------------------------------ */
function IndexNowPanel() {
  const [state, setState] = useState({ status: "idle", result: null });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const API = process.env.REACT_APP_BACKEND_URL;
  const token = () => (localStorage.getItem("hb_token") || "").trim();

  const run = async () => {
    setBusy(true);
    setErr("");
    try {
      const r = await fetch(`${API}/api/embed/admin/indexnow/ping`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token()}` },
      });
      if (!r.ok) throw new Error(`http ${r.status}`);
      const d = await r.json();
      setState({ status: d.ok ? "done" : "error", result: d });
    } catch (e) {
      setErr(String(e).slice(0, 200));
      setState({ status: "error", result: null });
    } finally {
      setBusy(false);
    }
  };

  const r = state.result || {};
  const badgeColor =
    state.status === "done" ? "bg-ink text-ivory" :
    state.status === "error" ? "bg-[hsl(var(--destructive))] text-white" :
    "bg-border text-muted-foreground";

  return (
    <div data-testid="indexnow-panel" className="mt-6 border border-border rounded-sm p-6">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="font-label text-[10px] tracking-widest text-rosemetal mb-1">
            ◉ INDEXNOW
          </div>
          <h3 className="font-display text-3xl mb-1">Ping search engines now</h3>
          <p className="font-body text-xs text-muted-foreground max-w-lg">
            Instantly notifies Bing, Yandex, Naver, and Seznam that key hub &amp; product URLs have changed.
            Search engines re-crawl within minutes instead of hours. Safe to run anytime after a publish.
          </p>
        </div>
        <button
          data-testid="indexnow-ping-btn"
          onClick={run}
          disabled={busy}
          className="bg-ink text-ivory px-6 py-3 font-label text-[10px] tracking-widest rounded-sm disabled:opacity-50"
        >
          {busy ? "Pinging…" : "Ping IndexNow"}
        </button>
      </div>

      <div className="mt-5 flex items-center gap-3 flex-wrap">
        <span data-testid="indexnow-status" className={`inline-block px-3 py-1 rounded-full font-label text-[9px] tracking-widest ${badgeColor}`}>
          {(state.status || "idle").toUpperCase()}
        </span>
        {state.status === "done" && r.keyed && (
          <span className="font-body text-xs text-muted-foreground">
            Pinged <b className="text-ink">{r.urls_pinged}</b> URLs · host <b className="text-ink">{r.host}</b>
          </span>
        )}
        {state.status === "done" && !r.keyed && (
          <span className="font-body text-xs text-[hsl(var(--destructive))]">
            INDEXNOW_KEY env var is not set on this server — nothing pinged. Add it in your backend .env and redeploy.
          </span>
        )}
        {err && <span className="font-body text-xs text-[hsl(var(--destructive))]">{err}</span>}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* AI Content generation panel                                        */
/* ------------------------------------------------------------------ */
function ContentGenPanel() {
  const [progress, setProgress] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [batchSize, setBatchSize] = useState(50);
  const [categorySlug, setCategorySlug] = useState("");
  const [force, setForce] = useState(false);
  const API = process.env.REACT_APP_BACKEND_URL;
  const token = () => (localStorage.getItem("hb_token") || "").trim();

  const fetchProgress = async () => {
    try {
      const r = await fetch(`${API}/api/admin/products/content-progress`, { headers: { Authorization: `Bearer ${token()}` } });
      if (!r.ok) throw new Error(`http ${r.status}`);
      const d = await r.json();
      setProgress(d);
    } catch (e) {
      setErr(String(e).slice(0, 200));
    }
  };

  useEffect(() => {
    fetchProgress();
    const t = setInterval(fetchProgress, 4000);
    return () => clearInterval(t);
  }, []);

  const start = async () => {
    setBusy(true);
    setErr("");
    try {
      const body = { limit: Number(batchSize), force };
      if (categorySlug) body.category_slug = categorySlug;
      const r = await fetch(`${API}/api/admin/products/regenerate-descriptions`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token()}`, "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (!r.ok) {
        const t = await r.text();
        throw new Error(t.slice(0, 300));
      }
      await fetchProgress();
    } catch (e) {
      setErr(String(e).slice(0, 300));
    } finally {
      setBusy(false);
    }
  };

  const job = progress?.job || {};
  const catalog = progress?.catalog || {};
  const pct = job.total > 0 ? Math.round((job.processed / job.total) * 100) : 0;
  const badgeColor = job.running ? "bg-champagne text-ink" : job.processed > 0 ? "bg-ink text-ivory" : "bg-border text-muted-foreground";

  return (
    <div data-testid="content-gen-panel" className="mt-6 border border-border rounded-sm p-6">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div className="max-w-xl">
          <div className="font-label text-[10px] tracking-widest text-rosemetal mb-1">◉ AI CONTENT · CLAUDE SONNET 4.6</div>
          <h3 className="font-display text-3xl mb-1">Regenerate product descriptions</h3>
          <p className="font-body text-xs text-muted-foreground">
            Rewrites the short + long descriptions plus one hook per color for the selected products.
            Editorial voice, spec-driven, unique across every color URL — fixes duplicate-content SEO
            and unlocks Google rich snippets. Costs about $0.01 per product.
          </p>
        </div>
      </div>

      {/* Coverage stats */}
      {progress && (
        <div className="mt-5 grid sm:grid-cols-4 gap-3 text-center">
          <div className="border border-border rounded-sm p-3">
            <div className="font-display text-2xl leading-none">{catalog.total_products || 0}</div>
            <div className="font-label text-[9px] tracking-widest text-muted-foreground mt-1">TOTAL PRODUCTS</div>
          </div>
          <div className="border border-border rounded-sm p-3 bg-cream/50">
            <div className="font-display text-2xl leading-none text-rosemetal">{catalog.products_with_ai_content || 0}</div>
            <div className="font-label text-[9px] tracking-widest text-muted-foreground mt-1">WITH AI CONTENT</div>
          </div>
          <div className="border border-border rounded-sm p-3">
            <div className="font-display text-2xl leading-none">{catalog.products_missing_ai_content || 0}</div>
            <div className="font-label text-[9px] tracking-widest text-muted-foreground mt-1">MISSING</div>
          </div>
          <div className="border border-border rounded-sm p-3">
            <div className="font-display text-2xl leading-none">{catalog.coverage_pct || 0}%</div>
            <div className="font-label text-[9px] tracking-widest text-muted-foreground mt-1">COVERAGE</div>
          </div>
        </div>
      )}

      {/* Job form */}
      <div className="mt-5 grid sm:grid-cols-[100px_180px_120px_auto] items-end gap-3">
        <div>
          <label className="block font-label text-[9px] tracking-widest text-muted-foreground mb-1">BATCH SIZE</label>
          <input type="number" min="1" max="2500" value={batchSize} onChange={(e)=>setBatchSize(e.target.value)} data-testid="content-batch-size" className="w-full bg-ivory border border-border rounded-sm px-3 py-2 font-body text-sm" />
        </div>
        <div>
          <label className="block font-label text-[9px] tracking-widest text-muted-foreground mb-1">CATEGORY (OPTIONAL)</label>
          <select value={categorySlug} onChange={(e)=>setCategorySlug(e.target.value)} data-testid="content-category" className="w-full bg-ivory border border-border rounded-sm px-3 py-2 font-body text-sm">
            <option value="">All</option>
            <option value="prom-dresses">Prom dresses</option>
            <option value="homecoming-dresses">Homecoming dresses</option>
            <option value="graduation-dresses">Graduation dresses</option>
            <option value="tuxedos">Tuxedos</option>
            <option value="accessories">Accessories</option>
            <option value="shoes">Shoes</option>
          </select>
        </div>
        <label className="inline-flex items-center gap-2 font-label text-[10px] tracking-widest">
          <input type="checkbox" checked={force} onChange={(e)=>setForce(e.target.checked)} data-testid="content-force" />
          FORCE REGEN
        </label>
        <button
          onClick={start}
          disabled={busy || job.running}
          data-testid="content-gen-start"
          className="bg-ink text-ivory px-6 py-3 font-label text-[10px] tracking-widest rounded-sm disabled:opacity-50"
        >
          {job.running ? "Running…" : busy ? "Starting…" : "Generate Now"}
        </button>
      </div>

      {/* Progress */}
      <div className="mt-5 flex items-center gap-3 flex-wrap">
        <span className={`inline-block px-3 py-1 rounded-full font-label text-[9px] tracking-widest ${badgeColor}`}>
          {job.running ? "RUNNING" : job.processed > 0 ? "IDLE · LAST RUN DONE" : "IDLE"}
        </span>
        {job.total > 0 && (
          <span className="font-body text-xs text-muted-foreground">
            <b className="text-ink">{job.processed}</b> / {job.total} · <b className="text-ink">{job.succeeded}</b> ok · <b className="text-[hsl(var(--destructive))]">{job.failed}</b> failed · <b>{pct}%</b>
          </span>
        )}
        {job.current_product && (
          <span className="font-body text-xs text-muted-foreground italic">
            → {job.current_product}
          </span>
        )}
        {job.last_error && (
          <span className="font-body text-xs text-[hsl(var(--destructive))] max-w-lg truncate" title={job.last_error}>
            last error: {job.last_error}
          </span>
        )}
        {err && <span className="font-body text-xs text-[hsl(var(--destructive))]">{err}</span>}
      </div>

      {job.total > 0 && (
        <div className="mt-3 h-1.5 bg-cream rounded-full overflow-hidden">
          <div className="h-full bg-rosemetal rounded-full transition-all" style={{ width: `${pct}%` }} />
        </div>
      )}
    </div>
  );
}


